/**
 * Data grouping utility functions for insights generation
 * Provides specialized grouping operations for insight detection algorithms
 */

import { DailyProfitSummary, HourlyProfitBreakdown } from '../api/profit';

/**
 * Group data by day of week with aggregated metrics
 */
export interface DayOfWeekGroup {
  dayOfWeek: number; // 0 = Sunday, 6 = Saturday
  dayName: string;
  netProfit: number;
  grossProfit: number;
  tradeCount: number;
  profitableDays: number;
  totalDays: number;
  winRate: number;
  avgProfitPerDay: number;
}

/**
 * Group data by hour of day
 */
export interface HourOfDayGroup {
  hour: number; // 0-23
  netProfit: number;
  grossProfit: number;
  tradeCount: number;
  avgProfitPerOccurrence: number;
  occurrenceCount: number; // Number of times this hour appeared in data
}

/**
 * Group data by asset/product
 */
export interface AssetGroup {
  productId: string;
  netProfit: number;
  grossProfit: number;
  tradeCount: number;
  avgProfitPerTrade: number;
  feesPaid: number;
}

/**
 * Group daily profit data by day of week
 * @param dailyData - Array of daily profit summaries
 * @returns Array of aggregated data grouped by day of week
 */
export function groupByDayOfWeek(dailyData: DailyProfitSummary[]): DayOfWeekGroup[] {
  if (!dailyData || dailyData.length === 0) {
    return [];
  }

  const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  
  // Group by day of week
  const dayMap = new Map<number, {
    netProfit: number;
    grossProfit: number;
    tradeCount: number;
    profitableDays: number;
    totalDays: number;
  }>();

  dailyData.forEach(day => {
    // Parse date in UTC to avoid timezone issues
    const date = new Date(day.trade_date + 'T00:00:00Z');
    const dayOfWeek = date.getUTCDay(); // 0 = Sunday, 6 = Saturday

    const existing = dayMap.get(dayOfWeek) || {
      netProfit: 0,
      grossProfit: 0,
      tradeCount: 0,
      profitableDays: 0,
      totalDays: 0,
    };

    existing.netProfit += day.net_profit;
    existing.grossProfit += day.gross_profit;
    existing.tradeCount += day.trade_count;
    existing.totalDays += 1;
    if (day.net_profit > 0) {
      existing.profitableDays += 1;
    }

    dayMap.set(dayOfWeek, existing);
  });

  // Convert to array and calculate derived metrics
  const groups: DayOfWeekGroup[] = Array.from(dayMap.entries()).map(([dayOfWeek, data]) => {
    const winRate = data.totalDays > 0 ? (data.profitableDays / data.totalDays) * 100 : 0;
    const avgProfitPerDay = data.totalDays > 0 ? data.netProfit / data.totalDays : 0;

    return {
      dayOfWeek,
      dayName: dayNames[dayOfWeek],
      netProfit: data.netProfit,
      grossProfit: data.grossProfit,
      tradeCount: data.tradeCount,
      profitableDays: data.profitableDays,
      totalDays: data.totalDays,
      winRate,
      avgProfitPerDay,
    };
  });

  // Sort by day of week (Monday-Sunday order for better readability)
  return groups.sort((a, b) => {
    const aDay = a.dayOfWeek === 0 ? 7 : a.dayOfWeek;
    const bDay = b.dayOfWeek === 0 ? 7 : b.dayOfWeek;
    return aDay - bDay;
  });
}

/**
 * Group hourly profit data by hour of day (0-23)
 * @param hourlyData - Array of hourly profit breakdowns
 * @returns Array of aggregated data grouped by hour (always returns 24 hours)
 */
export function groupByHour(hourlyData: HourlyProfitBreakdown[]): HourOfDayGroup[] {
  // Group by hour
  const hourMap = new Map<number, {
    netProfit: number;
    grossProfit: number;
    tradeCount: number;
    occurrenceCount: number;
  }>();

  // Only process data if we have some
  if (hourlyData && hourlyData.length > 0) {
    hourlyData.forEach(hour => {
      const existing = hourMap.get(hour.trade_hour) || {
        netProfit: 0,
        grossProfit: 0,
        tradeCount: 0,
        occurrenceCount: 0,
      };

      existing.netProfit += hour.net_profit;
      existing.grossProfit += hour.gross_profit;
      existing.tradeCount += hour.trade_count;
      existing.occurrenceCount += 1;

      hourMap.set(hour.trade_hour, existing);
    });
  }

  // Create complete 24-hour array (always return all hours, even if empty)
  const groups: HourOfDayGroup[] = [];
  for (let hour = 0; hour < 24; hour++) {
    const data = hourMap.get(hour) || {
      netProfit: 0,
      grossProfit: 0,
      tradeCount: 0,
      occurrenceCount: 0,
    };

    const avgProfitPerOccurrence = data.occurrenceCount > 0 
      ? data.netProfit / data.occurrenceCount 
      : 0;

    groups.push({
      hour,
      netProfit: data.netProfit,
      grossProfit: data.grossProfit,
      tradeCount: data.tradeCount,
      avgProfitPerOccurrence,
      occurrenceCount: data.occurrenceCount,
    });
  }

  return groups;
}

/**
 * Group daily profit data by asset/product
 * Only includes entries with a product_id
 * @param dailyData - Array of daily profit summaries (can include per-asset breakdowns)
 * @returns Array of aggregated data grouped by asset
 */
export function groupByAsset(dailyData: DailyProfitSummary[]): AssetGroup[] {
  if (!dailyData || dailyData.length === 0) {
    return [];
  }

  // Filter to only entries with product_id and group by asset
  const assetMap = new Map<string, {
    netProfit: number;
    grossProfit: number;
    tradeCount: number;
    feesPaid: number;
  }>();

  dailyData.forEach(day => {
    // Skip entries without product_id (those are totals)
    if (!day.product_id) {
      return;
    }

    const existing = assetMap.get(day.product_id) || {
      netProfit: 0,
      grossProfit: 0,
      tradeCount: 0,
      feesPaid: 0,
    };

    existing.netProfit += day.net_profit;
    existing.grossProfit += day.gross_profit;
    existing.tradeCount += day.trade_count;
    existing.feesPaid += day.fees_paid || 0;

    assetMap.set(day.product_id, existing);
  });

  // Convert to array and calculate derived metrics
  const groups: AssetGroup[] = Array.from(assetMap.entries()).map(([productId, data]) => {
    const avgProfitPerTrade = data.tradeCount > 0 ? data.netProfit / data.tradeCount : 0;

    return {
      productId,
      netProfit: data.netProfit,
      grossProfit: data.grossProfit,
      tradeCount: data.tradeCount,
      avgProfitPerTrade,
      feesPaid: data.feesPaid,
    };
  });

  // Sort by net profit (descending)
  return groups.sort((a, b) => b.netProfit - a.netProfit);
}
